import type { FC, PropsWithChildren } from 'react';
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as VOXLib from 'voximplant-websdk';
import type { Call } from 'voximplant-websdk/Call/Call';
import type { Client } from 'voximplant-websdk/Client';

import { useUpdatedRef } from '@trend-dev/ui-libs-utils/hooks/useUpdatedRef';

import { useCurrentUserStore } from 'store/currentUser';
import { useDebugStore } from 'store/debug';
import { userAdapter } from 'store/users/helpers/userAdapter';

import { DEVICE_PERMISSION } from 'YM/declare';
import { useSendErrorMetrics } from 'YM/hooks/useErrorMetrics';

import { EUnavailableType } from 'components/Pages/UnavailableContent/constants/unavailableType';
import { useUnavailableContent } from 'components/Pages/UnavailableContent/hooks/useUnavailableContent';

import { preparationPageManager } from 'manager/preparationPageManager';

import { provider } from 'providers';
import type { VideoCall } from 'providers/declare';

import { useConferenceId } from 'hooks/useConferenceId';
import { useRedirectToAuth } from 'hooks/useRedirectToAuth';

import { getDevicesPermissionMap } from 'utils/getDevicesPermissionMap';
import { getEnvironment } from 'utils/getEnvironment';

import { attachVOXAuthEvent } from './events/attachVOXAuthEvent';
import { attachVOXLocalMediaRendererAdded } from './events/attachVOXLocalMediaRendererAdded';
import { getURI } from './helpers/getURI';

interface IContext {
    /** VOX экземпляр конференции */
    VOXCallInstance: Call | null;
    /** VOX экземпляр клиента */
    VOXInstance: Client;
    /** Покинуть конференцию */
    leaveConference: () => Promise<boolean>;
    /** s */
    manageVideoCallInfo: VoidFunction;
    /** Установка информация о конференции */
    setVideoCall: (newState: VideoCall | null) => void;
    /** Информация о конференции */
    videoCall: VideoCall | null;
}

const Context = createContext<IContext>({
    VOXCallInstance: null,
    VOXInstance: VOXLib.getInstance(),
    leaveConference: () => Promise.resolve(false),
    manageVideoCallInfo: () => undefined,
    setVideoCall: () => undefined,
    videoCall: null,
});

export const useConferenceStore = () => useContext(Context);

const ConferenceStore: FC<PropsWithChildren> = ({ children }) => {
    const { currentUser, setCurrentUser } = useCurrentUserStore();
    const { current: VOXInstance } = useRef(VOXLib.getInstance());
    const [videoCall, setVideoCall] = useState<IContext['videoCall']>(null);
    const [VOXCallInstance, setVOXCallInstance] = useState<Call | null>(null);
    const [authorized, setAuthorized] = useState(false);
    const conferenceId = useConferenceId();
    const { redirectToUnavailableContentPage: redirectToOverPage } = useUnavailableContent();
    const { isDebugMode } = useDebugStore();
    const redirectToAuth = useRedirectToAuth();
    const environment = getEnvironment();
    const isLocalhost = environment === 'dev';
    const sendErrorMetrics = useSendErrorMetrics();
    const sendErrorMetricsRef = useUpdatedRef(sendErrorMetrics);

    async function initVOXSdk() {
        try {
            try {
                if (!VOXInstance.alreadyInitialized) {
                    await VOXInstance.init({ micRequired: true, showDebugInfo: true });
                }
            } catch (error) {
                sendErrorMetricsRef.current({ data: error, name: 'Инициализация VOX SDK' });
            }

            await VOXInstance.connect();
        } catch (error) {
            sendErrorMetricsRef.current({ data: error, name: 'Соединение с облаком VOX' });
        }

        try {
            let user = currentUser;

            if (!user) {
                const response = await provider.addUser();

                if ('_id' in response) {
                    user = userAdapter(response);
                } else {
                    throw new Error(JSON.stringify(response));
                }
            }

            const userId = user.id;

            attachVOXLocalMediaRendererAdded(userId, setCurrentUser);

            if (VOXInstance.getClientState() !== VOXLib.ClientState.LOGGED_IN) {
                attachVOXAuthEvent(VOXInstance, userId, setAuthorized);
            }

            try {
                const URI = getURI(userId);

                await VOXInstance.requestOneTimeLoginKey(URI).then(() => {
                    const isUserAlreadySet = !!currentUser;

                    if (!isUserAlreadySet) {
                        setCurrentUser(user);
                    }
                });
            } catch (error) {
                sendErrorMetricsRef.current({ data: error, name: 'Запрос одноразового пароля в VOX' });
            }
        } catch (error) {
            if (!isLocalhost) redirectToAuth();
        }
    }

    const manageVideoCallInfo = useCallback(async (conferenceId?: string) => {
        if (conferenceId) {
            const videoData = await provider.getVideoCallInfo(conferenceId);

            if ('_id' in videoData) {
                // конференция завершилась
                if (videoData.ended_at) return redirectToOverPage(EUnavailableType.IS_OVER);
            } else {
                // конференция не найдена
                return redirectToOverPage(EUnavailableType.NOT_FOUND);
            }

            return setVideoCall(videoData);
        }

        return undefined;
    }, []);

    async function joinConference(conferenceId: string) {
        try {
            await provider.joinVideoCall(conferenceId);
            const { camera } = await getDevicesPermissionMap(['camera']);

            const callInstance = VOXInstance.callConference({
                number: conferenceId,
                simulcast: true,
                video: { receiveVideo: true, sendVideo: camera === DEVICE_PERMISSION.ENABLED },
            });

            setVOXCallInstance(callInstance);
        } catch (error) {
            sendErrorMetricsRef.current({ data: error, name: 'Присоединение к конференции' });
        }
    }

    const leaveConference = useCallback((): Promise<boolean> => {
        return new Promise(resolve => {
            if (VOXCallInstance) {
                try {
                    VOXCallInstance.on(VOXLib.CallEvents.Disconnected, () => {
                        resolve(true);
                    });

                    VOXCallInstance.on(VOXLib.CallEvents.Failed, ({ code }: { code: number }) => {
                        // пользователь пытается присоединиться к конференции без разрешения на использование микрофона
                        if (code === 403) {
                            resolve(true);
                        }
                    });

                    try {
                        VOXCallInstance.muteMicrophone();
                    } catch (error) {
                        sendErrorMetricsRef.current({ data: error, name: 'Выключение микрофона при выходе из конференции' });
                    }

                    VOXInstance.showLocalVideo(false);
                    provider.leave(conferenceId!);
                    VOXCallInstance.hangup();
                    setVideoCall(null);
                    setVOXCallInstance(null);
                } catch (error) {
                    sendErrorMetricsRef.current({ data: error, name: 'Выход из конференции' });
                    resolve(false);
                }
            } else {
                sendErrorMetricsRef.current({ data: null, name: 'Попытка выхода из конференции перед записью экземпляра конференции в стейт' });
                resolve(false);
            }
        });
    }, [VOXCallInstance, conferenceId]);

    const contextValue = useMemo<IContext>(() => {
        return { VOXCallInstance, VOXInstance, leaveConference, manageVideoCallInfo, setVideoCall, videoCall };
    }, [leaveConference, videoCall, VOXCallInstance]);

    useEffect(() => {
        if (conferenceId && !isDebugMode) {
            initVOXSdk();
        }
    }, [conferenceId]);

    useEffect(() => {
        if (authorized && conferenceId && !isDebugMode) {
            manageVideoCallInfo(conferenceId);
        }
    }, [authorized, conferenceId]);

    useEffect(() => {
        if (authorized && conferenceId && !isDebugMode && preparationPageManager.isVisited && videoCall) {
            joinConference(conferenceId);
        }
    }, [authorized, conferenceId, videoCall]);

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default memo(ConferenceStore);
